// FILE: src/components/admin/studentStatus.jsx
// A student's statuses as the server computes them (WP-7, R2:
// src/services/studentStatus.js on the backend): `progressStatus` is one of
// invited / not-started / in-progress / completed and `statuses` adds
// locked, enrolment-ended and untimed. Always icon + words (MTO 5.4).
import React from "react";
import { Stack } from "@mui/material";
import MailOutlinedIcon from "@mui/icons-material/MailOutlined";
import PendingOutlinedIcon from "@mui/icons-material/PendingOutlined";
import HourglassEmptyOutlinedIcon from "@mui/icons-material/HourglassEmptyOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import TimerOffOutlinedIcon from "@mui/icons-material/TimerOffOutlined";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import { StatusChip } from "../ui";
import { UserStatusChips } from "./AdminBits";
import { auditReasonLabel, roleLabel, userStatusLabel } from "../../i18n/labels";

export const STUDENT_STATUS = {
  invited: { tone: "info", Icon: MailOutlinedIcon },
  "not-started": { tone: "default", Icon: PendingOutlinedIcon },
  "in-progress": { tone: "info", Icon: HourglassEmptyOutlinedIcon },
  completed: { tone: "success", Icon: TaskAltOutlinedIcon },
  locked: { tone: "error", Icon: LockOutlinedIcon },
  "enrolment-ended": { tone: "default", Icon: EventBusyOutlinedIcon },
  untimed: { tone: "warning", Icon: TimerOffOutlinedIcon }
};

/** The statuses users can be filtered on (GET /api/admin/users?status=). */
export const STATUS_FILTERS = ["all", "in-progress", "not-started", "completed", "invited", "locked", "enrolment-ended", "untimed"];

export function StudentStatusChip({ status }) {
  const style = STUDENT_STATUS[status];
  if (!style) return null;
  const { tone, Icon } = style;
  return <StatusChip tone={tone} icon={<Icon />} label={userStatusLabel(status)} />;
}

/**
 * Every status of an account as chips. Uses the server's `statuses` when the
 * payload has them (WP-7) and the older field-by-field view otherwise.
 */
export function StudentStatusChips({ user }) {
  if (!user) return null;
  if (!Array.isArray(user.statuses)) return <UserStatusChips user={user} />;
  return (
    <Stack direction="row" sx={{ flexWrap: "wrap", gap: 0.75 }}>
      {user.role && user.role !== "student" && (
        <StatusChip tone="info" icon={<AdminPanelSettingsOutlinedIcon />} label={roleLabel(user.role)} />
      )}
      {user.statuses.map((s) => (
        <StudentStatusChip key={s} status={s} />
      ))}
    </Stack>
  );
}

/**
 * Why an account is locked: a system code (MAX_PASSWORD_ATTEMPTS …) in words,
 * or the reason staff typed when they locked it, exactly as written.
 */
export function lockReasonText(reason) {
  if (typeof reason !== "string" || !reason.trim()) return "";
  return /^[A-Z0-9_]+$/.test(reason.trim()) ? auditReasonLabel(reason.trim()) : reason.trim();
}
